/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, type HTMLAttributes, createContext, forwardRef } from 'react';
import { Button as ButtonCore, type ContextValue, Group as GroupCore, MenuTrigger, useContextProps } from 'react-aria-components';
import { breakpoints } from '../../constants.stylex';
import { useMediaQuery } from '../../hooks/use-media-query/use-media-query.hook';
import { ArrowSolidDownFillIcon } from '../../icons/arrow-solid-down-fill/arrow-solid-down-fill.icon';
import { ChevronLeftStrokeIcon } from '../../icons/chevron-left-stroke/chevron-left-stroke.icon';
import { ChevronRightStrokeIcon } from '../../icons/chevron-right-stroke/chevron-right-stroke.icon';
import { $ } from '../../tokens.stylex';
import { IconButton } from '../icon-button/icon-button.component';
import { Input } from '../input/input.component';
import { MenuItem } from '../menu-item/menu-item.component';
import { Menu } from '../menu/menu.component';
import { NumberField } from '../number-field/number-field.component';
import { Popover } from '../popover/popover.component';
import { Text } from '../text/text.component';
import { View } from '../view/view.component';

interface TablePaginationProps extends Omit<HTMLAttributes<HTMLDivElement>, 'style' | 'className'> {
  currentPage: number;
  pageSize: number;
  totalResults: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Total item count after which the page controls are hidden. @default 30 */
  pageControlsHideThreshold?: number;
  pageSizeOptions?: number[];
  style?: stylex.StyleXStyles;
}

const TablePaginationContext = createContext<ContextValue<TablePaginationProps, HTMLDivElement>>({});


const TablePagination = forwardRef((props: TablePaginationProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, TablePaginationContext);

  const isDesktop = useMediaQuery(breakpoints.large);

  const {
    style,
    currentPage,
    pageSize,
    totalResults,
    pageSizeOptions = [25, 50, 100, 200],
    pageControlsHideThreshold = 30,
    onPageChange,
    onPageSizeChange,
    ...coreProps
  } = props;

  const totalPages = Math.ceil(totalResults / pageSize);

  if (totalResults === 0) {
    return;
  }

  return (
    <div
      ref={ref}
      {...stylex.props(styles.base, style)}
      {...coreProps}>
      {totalResults > pageControlsHideThreshold &&
        <GroupCore {...stylex.props(groupStyles.base)}>
          <IconButton
            variant="ghost"
            onPress={() => onPageChange(currentPage - 1)}
            isRounded
            size={isDesktop ? 28 : 32}
            isDisabled={currentPage === 1}
            style={(renderProps) => renderProps.isDisabled && iconButtonStyles.disabled}
            aria-label="Previous page">
            <ChevronLeftStrokeIcon />
          </IconButton>

          <View {...stylex.props(pageInfoStyles.base)}>
            <Text size={14} color="quaternary">Page</Text>

            <NumberField
              value={currentPage}
              onChange={onPageChange}
              minValue={1}
              maxValue={totalPages}
              aria-label="Page number">
              <Input size={32} style={pageNumberStyles.input} />
            </NumberField>

            <Text size={14} color="quaternary">of {Intl.NumberFormat().format(totalPages)}</Text>
          </View>

          <IconButton
            variant="ghost"
            onPress={() => onPageChange(currentPage + 1)}
            isRounded
            size={isDesktop ? 28 : 32}
            isDisabled={currentPage === totalPages}
            style={(renderProps) => renderProps.isDisabled && iconButtonStyles.disabled}
            aria-label="Next page">
            <ChevronRightStrokeIcon />
          </IconButton>
        </GroupCore>}

      <View {...stylex.props(resultsSummaryStyles.base)}>
        {totalResults > pageControlsHideThreshold ?
          <>
            <Text size={14} color="placeholder">Showing</Text>

            <MenuTrigger>
              <ButtonCore {...stylex.props(pageSelector.trigger)}>
                {(renderProps) =>
                  <>
                    <Text size={14} color="tertiary">{pageSize}</Text>
                    <ArrowSolidDownFillIcon
                      size={6}
                      color="secondary"
                      style={[pageSelector.indicator, renderProps.isPressed && pageSelector.indicatorRotated]}
                    />
                  </>}
              </ButtonCore>

              <Popover placement="top start" crossOffset={-17}>
                <Menu
                  items={pageSizeOptions.map((size) => ({ id: size, label: size }))}
                  selectionMode="single"
                  selectedKeys={[pageSize]}
                  onSelectionChange={(keys) => onPageSizeChange(keys[Symbol.iterator]().next().value as number)}
                  style={pageSelector.menu}>
                  {(item) => <MenuItem id={item.id} size={40}>{item.label}</MenuItem>}
                </Menu>
              </Popover>
            </MenuTrigger>

            <Text size={14} color="placeholder">of {Intl.NumberFormat().format(totalResults)} results</Text>
          </> :
          <Text size={14} color="placeholder">{Intl.NumberFormat().format(totalResults)} results</Text>}
      </View>
    </div>
  );
});

const styles = stylex.create({
  base: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'grid',
    gridRowGap: {
      default: 0,
      [breakpoints.small]: 12,
    },
    gridTemplateColumns: {
      default: '1fr 1fr',
      [breakpoints.small]: '1fr',
    },
    borderTopColor: $.border04,
    borderTopStyle: 'solid',
    borderTopWidth: 1,
    minHeight: 53,
    paddingBottom: {
      default: 10,
      [breakpoints.medium]: 34,
    },
    paddingLeft: 12,
    paddingRight: 20,
    paddingTop: 10,
    width: '100%',
  },
});

const pageInfoStyles = stylex.create({
  base: {
    gap: 8,
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
  },
});

const iconButtonStyles = stylex.create({
  disabled: {
    backgroundColor: 'transparent',
  }
});

const groupStyles = stylex.create({
  base: {
    gap: 8,
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: {
      default: 'flex-start',
      [breakpoints.small]: 'center',
    }
  },
});

const pageNumberStyles = stylex.create({
  input: {
    paddingLeft: {
      default: 8,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 7,
    },
    paddingRight: {
      default: 8,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 7,
    },
    width: 40,
  }
});

const resultsSummaryStyles = stylex.create({
  base: {
    gap: 6,
    gridColumn: {
      default: '2',
      [breakpoints.small]: '1',
    },
    alignItems: 'center',
    display: 'flex',
    justifyContent: {
      default: 'flex-end',
      [breakpoints.small]: 'center',
    }
  }
});

const pageSelector = stylex.create({
  trigger: {
    margin: 0,
    padding: 0,
    borderStyle: 'none',
    gap: 2,
    alignItems: 'center',
    backgroundColor: 'transparent',
    display: 'flex',
  },
  indicator: { transition: 'transform 0.15s ease-in' },
  indicatorRotated: { transform: 'rotate(180deg)' },
  menu: { width: 80 },
});

export { TablePagination, TablePaginationContext, type TablePaginationProps };
